import React, { useState } from 'react'
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'
import { Eye } from 'lucide-react'
import {
  CreateCompanyModal,
  type GroupOption,
  type CreateCompanyInput,
  type ParentCompanyOption,
} from '../modals/CreateCompanyModal'
import { DepositWalletsDrawer, type DepositWallet } from '../drawers/DepositWalletsDrawer'
import { AssignUserToCompanyDialog, type CompanyOption } from '../dialogs/AssignUserToCompanyDialog'
import { ConfirmationDialog } from '../dialogs/ConfirmationDialog'
import { type CompanyData } from './CompaniesTabView'
import { SkeletonTable } from '../skeleton/Skeleton'
import { CompanyRoleBadge } from '../badges/status'
import { FallbackCompanyIcon } from '../assets/fallback'

export interface CompaniesTableProps {
  companies: CompanyData[]
  loading?: boolean
  showCreateButton?: boolean
  showAssignButton?: boolean
  showWalletsButton?: boolean
  showUnassignButton?: boolean
  preSelectedGroup?: GroupOption

  // Data for modals
  groups?: GroupOption[]
  allCompanies?: CompanyOption[] // For assign dialog
  depositWallets?: DepositWallet[]
  walletsLoading?: boolean

  // Callbacks
  onCreateCompany?: (input: CreateCompanyInput) => Promise<void>
  onAssignUserToCompany?: (userId: string, companyIds: string[]) => Promise<void>
  onUnassignUserFromCompany?: (userId: string, companyId: string) => Promise<void>
  onViewCompanyDetails?: (companyId: string) => void
  onFetchWallets?: (companyId: string) => void
  onCopyWalletAddress?: (address: string) => void

  // Context data
  userId?: string // For assign/unassign operations

  /** Admin-only: show auto-approve toggle on create company modal. */
  showAutoApproveToggle?: boolean

  // Modal trigger handlers (exposed to parent)
  onModalHandlersReady?: (handlers: {
    handleCreateClick: () => void
    handleAssignClick: () => void
  }) => void
}

export function CompaniesTable({
  companies,
  loading = false,
  showCreateButton = false,
  showAssignButton = false,
  showWalletsButton = false,
  showUnassignButton = false,
  groups = [],
  allCompanies = [],
  depositWallets = [],
  walletsLoading = false,
  onCreateCompany,
  onAssignUserToCompany,
  onUnassignUserFromCompany,
  onViewCompanyDetails,
  onFetchWallets,
  onCopyWalletAddress,
  userId,
  showAutoApproveToggle = false,
  onModalHandlersReady,
  preSelectedGroup,
}: CompaniesTableProps) {
  const parentCompanyCandidates: ParentCompanyOption[] = companies.map((company) => ({
    id: company.id,
    name: company.company,
    groupId: company.groupId,
    parentCompanyId: company.parentCompanyId,
  }))

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isWalletsDrawerOpen, setIsWalletsDrawerOpen] = useState(false)
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false)
  const [isUnassignConfirmOpen, setIsUnassignConfirmOpen] = useState(false)

  // Selected items
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string
    name: string
    group: string
    companyImageUrl?: string
  } | null>(null)
  const [companyToUnassign, setCompanyToUnassign] = useState<{
    id: string
    name: string
  } | null>(null)

  // Loading states
  const [isUnassigning, setIsUnassigning] = useState(false)

  // Handler functions
  const handleOpenWalletsDrawer = (company: CompanyData) => {
    setSelectedCompany({
      id: company.id,
      name: company.company,
      group: company.group,
      companyImageUrl: company.imageUrl,
    })
    setIsWalletsDrawerOpen(true)
    if (onFetchWallets) {
      onFetchWallets(company.id)
    }
  }

  const handleUnassignCompany = (companyId: string, companyName: string) => {
    setCompanyToUnassign({ id: companyId, name: companyName })
    setIsUnassignConfirmOpen(true)
  }

  const handleConfirmUnassign = async () => {
    if (!userId || !companyToUnassign || !onUnassignUserFromCompany) return

    setIsUnassigning(true)
    try {
      await onUnassignUserFromCompany(userId, companyToUnassign.id)
      setIsUnassignConfirmOpen(false)
      setCompanyToUnassign(null)
    } catch (_error) {
      // Error handling done by parent
    } finally {
      setIsUnassigning(false)
    }
  }

  const handleAssignUser = async (userIdToAssign: string, companyIds: string[]) => {
    if (onAssignUserToCompany) {
      await onAssignUserToCompany(userIdToAssign, companyIds)
    }
  }

  const handleCreateCompanySubmit = async (input: CreateCompanyInput) => {
    if (onCreateCompany) {
      await onCreateCompany(input)
    }
  }

  // Handle external modal triggers from header
  const handleCreateClick = () => setIsCreateModalOpen(true)
  const handleAssignClick = () => setIsAssignDialogOpen(true)

  // Expose handlers to parent on mount
  React.useEffect(() => {
    if (onModalHandlersReady) {
      onModalHandlersReady({
        handleCreateClick,
        handleAssignClick,
      })
    }
  }, [onModalHandlersReady])

  if (loading) {
    return <SkeletonTable />
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="h-[60px]">
              <TableHead className="w-[100px]">ID</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Auto Deposit</TableHead>
              <TableHead>Trade Type</TableHead>
              <TableHead className="w-[100px]"></TableHead>
              <TableHead className="w-[100px]"></TableHead>
              <TableHead className="w-[100px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map((company) => (
              <TableRow key={company.id} className="h-[60px]">
                <TableCell className="text-sm">{company.id}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6 rounded-sm border border-card-border">
                      <AvatarImage src={company.imageUrl} alt={company.company} />
                      <AvatarFallback className="rounded-sm bg-gray-200">
                        <FallbackCompanyIcon />
                      </AvatarFallback>
                    </Avatar>

                    {company.company}
                  </div>
                </TableCell>
                <TableCell>
                  {company.group !== '-' ? (
                    company.group
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>${company.balance}</TableCell>
                <TableCell>{company.autoApprove ? 'ON' : 'OFF'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <CompanyRoleBadge tradeType={company.tradeType} />
                  </div>
                </TableCell>

                {(() => {
                  const buttons = []

                  if (showWalletsButton) {
                    buttons.push(
                      <Button
                        key="wallets"
                        variant="main_outline"
                        className="rounded-full px-6 h-7 text-xs min-w-[150px]"
                        onClick={() => handleOpenWalletsDrawer(company)}
                      >
                        Wallets
                      </Button>,
                    )
                  }

                  if (showUnassignButton) {
                    buttons.push(
                      <Button
                        key="unassign"
                        variant="destructive"
                        size="sm"
                        className="min-w-[150px]"
                        onClick={() => handleUnassignCompany(company.id, company.company)}
                      >
                        Unassign
                      </Button>,
                    )
                  }

                  if (onViewCompanyDetails) {
                    buttons.push(
                      <Button
                        key="view"
                        variant="main_action"
                        className="rounded-full px-6 h-7 text-xs min-w-[150px]"
                        onClick={() => onViewCompanyDetails(company.id)}
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>,
                    )
                  }

                  // Create array of 3 cells, positioning buttons from the end
                  const cells: (React.ReactElement | null)[] = [null, null, null]
                  buttons.forEach((button, index) => {
                    const position = 3 - buttons.length + index
                    cells[position] = button
                  })

                  return cells.map((button, index) => <TableCell key={index}>{button}</TableCell>)
                })()}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Modals and Dialogs */}
      {showCreateButton && onCreateCompany && (
        <CreateCompanyModal
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          groups={groups}
          parentCompanyCandidates={parentCompanyCandidates}
          loading={loading}
          onSubmit={handleCreateCompanySubmit}
          preSelectedGroup={preSelectedGroup}
          showAutoApproveToggle={showAutoApproveToggle}
        />
      )}

      {selectedCompany && (
        <DepositWalletsDrawer
          open={isWalletsDrawerOpen}
          onOpenChange={setIsWalletsDrawerOpen}
          companyImageUrl={selectedCompany.companyImageUrl}
          companyName={selectedCompany.name}
          // groupName={selectedCompany.group}
          depositWallets={depositWallets}
          loading={walletsLoading}
          onCopyAddress={onCopyWalletAddress}
        />
      )}

      {userId && showAssignButton && onAssignUserToCompany && (
        <AssignUserToCompanyDialog
          open={isAssignDialogOpen}
          onOpenChange={setIsAssignDialogOpen}
          userId={userId}
          companies={allCompanies}
          groups={groups}
          loading={loading}
          onAssign={handleAssignUser}
        />
      )}

      {userId && companyToUnassign && showUnassignButton && (
        <ConfirmationDialog
          open={isUnassignConfirmOpen}
          onOpenChange={setIsUnassignConfirmOpen}
          title="Confirm Unassign"
          description={`Are you sure you want to unassign this user from "${companyToUnassign.name}"? This action cannot be undone.`}
          confirmText="Unassign"
          cancelText="Cancel"
          onConfirm={handleConfirmUnassign}
          isLoading={isUnassigning}
          variant="destructive"
        />
      )}
    </div>
  )
}
